import { isNil } from 'lodash-es';
import { type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { ZoomToSelectionIcon } from '@dynatrace/strato-icons';

import { INTERACTION_MODE } from '../../../core/components/interactions-overlay/types.js';
import { calculateInitialSelectionAreaPositions } from '../../../core/components/interactions-overlay/utils/calculate-initial-selection-area-positions.js';
import {
  reduceExploreCancelAll,
  reduceExploreSelect,
} from '../../../core/components/interactions-state-helpers/reducers/explore-reducers.js';
import {
  reduceInteractionMode,
  reduceSelectionAreaVariant,
} from '../../../core/components/interactions-state-helpers/reducers/mode-reducers.js';
import { ChartToolbarRenderer } from '../../../core/components/toolbar/ChartToolbarRenderer.js';
import { useToolbarContext } from '../../../core/components/toolbar/hooks/useToolbarContext.js';
import { TOOLBAR_MESSAGES } from '../../../core/components/toolbar/messages.js';
import { useA11yToolbarTabIndex } from '../../../core/hooks/useA11yToolbarTabIndex.js';
import { getKeyShortcutByAgent } from '../../../core/utils/get-key-shortcut-by-agent.js';
import {
  useInteractionModeState,
  useSelectState,
} from '../../hooks/store/selectors.js';
import { useSetState } from '../../hooks/store/state-setters.js';
import { useHistogramChartOverlayRef } from '../../hooks/useHistogramChartOverlayRef.js';
import { HISTOGRAM_MESSAGES } from '../../messages/tooltip-text.js';

export const ZoomModeButton = () => {
  const setState = useSetState();
  const interactionMode = useInteractionModeState();
  const tabIndex = useA11yToolbarTabIndex(INTERACTION_MODE.Zoom);
  const overlayRef = useHistogramChartOverlayRef();
  const intl = useIntl();
  const tooltipShortcut =
    getKeyShortcutByAgent('CONTROL') +
    ' ' +
    intl.formatMessage(TOOLBAR_MESSAGES.or) +
    ' Z';
  const { updateLastItemSelected } = useToolbarContext();
  const isSelected = interactionMode === INTERACTION_MODE.Zoom;

  const selectState = useSelectState();

  const createInitialZoomArea = () => {
    if (!isNil(overlayRef.current)) {
      const bounds = overlayRef.current.getBoundingClientRect();

      const [start, end] = calculateInitialSelectionAreaPositions(bounds);

      setState(
        reduceExploreSelect({
          start,
          end,
          finished: true,
          accessible: true,
          variant: 'zoom',
          isKeyboardCreated: true,
        }),
      );
    }
  };

  const handleKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'Enter') {
      createInitialZoomArea();
    }
  };

  const handleClick = () => {
    if (interactionMode !== 'zoom') {
      setState(reduceInteractionMode(INTERACTION_MODE.Zoom));
    }

    if (selectState?.start && selectState?.end) {
      if (selectState?.isKeyboardCreated) {
        reduceSelectionAreaVariant('zoom');
      } else {
        setState(reduceExploreCancelAll);
      }
    }

    updateLastItemSelected(INTERACTION_MODE.Zoom);
  };

  return (
    <ChartToolbarRenderer.Control
      prefixIcon={<ZoomToSelectionIcon />}
      selected={isSelected}
      tooltipText={intl.formatMessage(HISTOGRAM_MESSAGES.zoomModeZoom)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={tabIndex}
      tooltipKeyShortcut={tooltipShortcut}
      placement="menu"
    />
  );
};
